import { ApiError } from "./client.js";
import { getClientId } from "./realtimeClientId.js";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3000";

function readCookie(name) {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
}

async function request(method, path, body) {
  const csrfToken = readCookie("casual_csrf");
  const isMutating = method !== "GET";
  const res = await fetch(`${API_URL}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
      ...(isMutating ? { "X-Realtime-Client-Id": getClientId() } : {}),
    },
    credentials: "include",
    body: method === "GET" ? undefined : JSON.stringify(body ?? {}),
  });
  const contentType = res.headers.get("content-type") || "";
  const data = contentType.includes("application/json") ? await res.json().catch(() => null) : null;
  if (!res.ok) throw new ApiError(res.status, data);
  return data;
}

const getJson = (path) => request("GET", path);
const putJson = (path, body) => request("PUT", path, body);
const sendJson = (method, path, body) => request(method, path, body);

function withVersion(path, version) {
  return `${path}?version=${encodeURIComponent(version)}`;
}

export const casualData = {
  getProfile: () => getJson("/api/casual/profile"),
  putProfile: (data) => putJson("/api/casual/profile", data),
  getEstimates: () => getJson("/api/casual/estimates"),
  putEstimates: (data) => putJson("/api/casual/estimates", data),

  getAdminAccounts: () => getJson("/api/casual/admin-accounts"),
  createAdminAccount: (email) => sendJson("POST", "/api/casual/admin-accounts", { email }),
  updateAdminAccount: (id, data) => sendJson("PATCH", `/api/casual/admin-accounts/${id}`, data),
  removeAdminAccount: (id) => sendJson("DELETE", `/api/casual/admin-accounts/${id}`),

  // ── Kainininkas (granuliuota: kiekvienas veiksmas — savo REST kvietimas,
  // ne visos struktūros PUT — žr. plano dokumentą). Gamintojas = 1-as lygis,
  // Tiekėjas nebėra struktūrinis lygis — žr. row-supplier kvietimus žemiau. ──
  getKainininkasTree: () => getJson("/api/casual/kainininkas"),
  getKnownSuppliers: () => getJson("/api/casual/kainininkas/known-suppliers"),
  searchKainininkasRows: (q) => getJson(`/api/casual/kainininkas/search-rows?q=${encodeURIComponent(q)}`),
  createManufacturer: (name) => sendJson("POST", "/api/casual/kainininkas/manufacturers", { name }),
  renameManufacturer: (id, name, version) => sendJson("PATCH", `/api/casual/kainininkas/manufacturers/${id}`, { name, version }),
  deleteManufacturer: (id, version) => sendJson("DELETE", withVersion(`/api/casual/kainininkas/manufacturers/${id}`, version)),
  createGroup: (manufacturerId, name, templateTableId) => sendJson("POST", "/api/casual/kainininkas/groups", { manufacturerId, name, templateTableId }),
  renameGroup: (id, name, version) => sendJson("PATCH", `/api/casual/kainininkas/groups/${id}`, { name, version }),
  deleteGroup: (id, version) => sendJson("DELETE", withVersion(`/api/casual/kainininkas/groups/${id}`, version)),

  // ── Eilutės tiekėjai (viena prekė gali turėti kelis tiekėjus — tik Kainininkui) ──
  createRowSupplier: (tableId, rowId, entry) => sendJson("POST", `/api/casual/price-tables/${tableId}/rows/${rowId}/suppliers`, entry),
  updateRowSupplier: (tableId, rowId, id, patch, version) => sendJson("PATCH", `/api/casual/price-tables/${tableId}/rows/${rowId}/suppliers/${id}`, { ...patch, version }),
  deleteRowSupplier: (tableId, rowId, id, version) => sendJson("DELETE", withVersion(`/api/casual/price-tables/${tableId}/rows/${rowId}/suppliers/${id}`, version)),

  // ── Duomenų matrica (sąrašas + sukūrimas; pervardinimas/trynimas per price-tables) ──
  listDataMatrixTables: () => getJson("/api/casual/data-matrix"),
  createDataMatrixTable: (name, templateTableId) => sendJson("POST", "/api/casual/data-matrix", { name, templateTableId }),

  // ── Kainininko lentelė (bendra abiem funkcijoms — žr. price-tables/routes.ts) ──
  getPriceTable: (id) => getJson(`/api/casual/price-tables/${id}`),
  renameTable: (id, name, version) => sendJson("PATCH", `/api/casual/price-tables/${id}`, { name, version }),
  deleteTable: (id, version) => sendJson("DELETE", withVersion(`/api/casual/price-tables/${id}`, version)),
  applyTemplate: (tableId, templateTableId, version) => sendJson("POST", `/api/casual/price-tables/${tableId}/apply-template`, { templateTableId, version }),
  createColumn: (tableId, column) => sendJson("POST", `/api/casual/price-tables/${tableId}/columns`, column),
  updateColumn: (tableId, id, patch, version) => sendJson("PATCH", `/api/casual/price-tables/${tableId}/columns/${id}`, { ...patch, version }),
  deleteColumn: (tableId, id, version) => sendJson("DELETE", withVersion(`/api/casual/price-tables/${tableId}/columns/${id}`, version)),
  reorderColumn: (tableId, id, direction, version) => sendJson("PATCH", `/api/casual/price-tables/${tableId}/columns/${id}/reorder`, { direction, version }),
  createRow: (tableId) => sendJson("POST", `/api/casual/price-tables/${tableId}/rows`, {}),
  patchRowCells: (tableId, id, cells, version) => sendJson("PATCH", `/api/casual/price-tables/${tableId}/rows/${id}`, { cells, version }),
  linkRow: (tableId, rowId, linkedRowId, version) => sendJson("PATCH", `/api/casual/price-tables/${tableId}/rows/${rowId}/link`, { linkedRowId, version }),
  deleteRow: (tableId, id, version) => sendJson("DELETE", withVersion(`/api/casual/price-tables/${tableId}/rows/${id}`, version)),
};
